import React from 'react';
import { Box, Typography, Slider, Button } from '@mui/material';
import { Star, StarBorder } from '@mui/icons-material';
import { QuestionComponentProps } from '../../types';
import { CommentField } from '../CommentField';

export const RangeQuestion: React.FC<QuestionComponentProps> = ({
  question,
  value,
  comment,
  language,
  onChange,
  onCommentChange,
  disabled = false,
  error,
  autoFocus: _autoFocus = false,
}) => {
  const getLocalizedText = (textRecord: Record<string, string>): string => {
    return textRecord[language] || textRecord['en'] || textRecord[Object.keys(textRecord)[0]] || '';
  };

  const minValue = question.validationRules?.minValue || 1;
  const maxValue = question.validationRules?.maxValue || 5;
  const currentValue = (value as number) || 0;

  const handleSliderChange = (_: Event, newValue: number | number[]) => {
    if (!disabled && typeof newValue === 'number') {
      onChange(newValue);
    }
  };

  const handleStarClick = (starValue: number) => {
    if (!disabled) {
      onChange(starValue);
    }
  };

  const handleScaleClick = (scaleValue: number) => {
    if (!disabled) {
      onChange(scaleValue);
    }
  };

  const renderStarRating = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, my: 3 }}>
      {Array.from({ length: maxValue }, (_, i) => i + 1).map((star) => (
        <Button
          key={star}
          onClick={() => handleStarClick(star)}
          disabled={disabled}
          sx={{ minWidth: 'auto', p: 0.5 }}
          data-testid={`star-${star}`}
        >
          {currentValue >= star ? (
            <Star sx={{ fontSize: 32, color: 'primary.main' }} />
          ) : (
            <StarBorder sx={{ fontSize: 32, color: 'action.disabled' }} />
          )}
        </Button>
      ))}
    </Box>
  );

  const renderScaleButtons = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, my: 3 }}>
      {Array.from({ length: maxValue - minValue + 1 }, (_, i) => minValue + i).map((scale) => (
        <Button
          key={scale}
          variant={currentValue === scale ? 'contained' : 'outlined'}
          onClick={() => handleScaleClick(scale)}
          disabled={disabled}
          sx={{ minWidth: 48, height: 48, borderRadius: '50%' }}
          data-testid={`scale-${scale}`}
        >
          {scale}
        </Button>
      ))}
    </Box>
  );

  const renderSlider = () => (
    <Box sx={{ width: '100%', maxWidth: 400, px: 2, my: 3 }}>
      <Slider
        value={currentValue || minValue}
        onChange={handleSliderChange}
        min={minValue}
        max={maxValue}
        step={1}
        marks
        valueLabelDisplay="auto"
        disabled={disabled}
        data-testid="range-slider"
      />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {minValue}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {maxValue}
        </Typography>
      </Box>
    </Box>
  );

  // Simple rendering - use stars if maxValue is 5, scale buttons if ≤ 7, otherwise slider
  const renderRangeInput = () => {
    if (maxValue === 5 && minValue === 1) {
      return renderStarRating();
    } else if (maxValue - minValue <= 6) {
      return renderScaleButtons();
    } else {
      return renderSlider();
    }
  };

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', py: 3 }}
    >
      <Typography variant="h5" sx={{ textAlign: 'center', mb: 2 }}>
        {getLocalizedText(question.title)}
      </Typography>

      {question.description && (
        <Typography sx={{ textAlign: 'center', mb: 4, maxWidth: 500, color: 'text.secondary' }}>
          {getLocalizedText(question.description)}
        </Typography>
      )}

      {renderRangeInput()}

      {question.hasCommentField && onCommentChange && (
        <Box sx={{ maxWidth: 500, width: '100%' }}>
          <CommentField
            value={comment}
            onChange={onCommentChange}
            language={language}
            disabled={disabled}
            placeholder={
              question.commentFieldDescription
                ? getLocalizedText(question.commentFieldDescription)
                : undefined
            }
          />
        </Box>
      )}

      {error && (
        <Typography
          color="error"
          variant="body2"
          sx={{ textAlign: 'center', mt: 2 }}
          role="alert"
          data-testid="error-message"
        >
          {error}
        </Typography>
      )}
    </Box>
  );
};
